// src/components/dayClose/RipplesVisualizationStep.tsx
// Step 3: Ripples Visualization - 顯示今天的貢獻

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Target, Scale } from 'lucide-react';
import { DayCloseStepProps, DayCloseRipples } from '../../types/dayClose';
import { OBJECTIVE_CATEGORY_CONFIG } from '../../features/goal/types/objective';
import { getDayCloseById } from '../../services/api/dayClose';
import { logger } from '../../utils/logger';
import { RipplesSkeleton } from './RipplesSkeleton';
import { AnimatedRippleNumber } from './AnimatedRippleNumber';
import { TotalScoreCard } from './TotalScoreCard';
import { LifeBalanceBar } from './LifeBalanceBar';

const INITIAL_DISPLAY_COUNT = 6;  // 初始顯示的卡片數量

export const RipplesVisualizationStep: React.FC<DayCloseStepProps> = ({
  dayCloseId,
  onComplete,
  userId,
  setStepHandlers
}) => {
  const [loading, setLoading] = useState(true);
  const [ripples, setRipples] = useState<DayCloseRipples | null>(null);
  const [showAllObjectives, setShowAllObjectives] = useState(false);

  // 載入 Ripples 數據
  useEffect(() => {
    const loadRipples = async () => {
      try {
        setLoading(true);
        const dayClose = await getDayCloseById(dayCloseId);

        if (dayClose?.ripples) {
          setRipples(dayClose.ripples);
        }
      } catch (error) {
        logger.error('RipplesVisualization', 'Failed to load ripples', error);
      } finally {
        setLoading(false);
      }
    };

    loadRipples();
  }, [dayCloseId]);

  // 持久化 Ripples 並完成
  const handleContinue = useCallback(async () => {
    try {
      logger.info('RipplesVisualization', 'Persisting ripples to database');

      const { completeDayClose } = await import('../../services/api/completeDayClose');
      await completeDayClose({ dayCloseId });

      logger.info('RipplesVisualization', 'Ripples persisted successfully');
      onComplete();
    } catch (error) {
      logger.error('RipplesVisualization', 'Failed to persist ripples', error);
      // 即使持久化失敗也繼續（避免阻塞用戶）
      onComplete();
    }
  }, [dayCloseId, onComplete]);

  // 註冊步驟處理函數到 Modal
  useEffect(() => {
    if (setStepHandlers) {
      setStepHandlers({
        handleReflection: handleContinue
      });
    }

    return () => {
      if (setStepHandlers) {
        setStepHandlers({});
      }
    };
  }, [handleContinue, setStepHandlers]);

  if (loading) {
    return <RipplesSkeleton />;
  }

  if (!ripples) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No ripples data available</p>
      </div>
    );
  }

  const objectives = ripples.objectives || [];
  const dimensions = ripples.lifeDimensions || [];

  // 只顯示有分數的維度
  const activeDimensions = dimensions.filter(d => d.ripples > 0);

  // 計算今日總分
  const totalRipples =
    objectives.reduce((sum, obj) => sum + obj.ripples, 0) +
    dimensions.reduce((sum, dim) => sum + dim.ripples, 0);

  const visibleObjectives = showAllObjectives
    ? objectives
    : objectives.slice(0, INITIAL_DISPLAY_COUNT);

  const hasMoreObjectives = objectives.length > INITIAL_DISPLAY_COUNT;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">

      {/* 總分卡片 */}
      <TotalScoreCard totalRipples={totalRipples} />

      {/* 並排佈局：Goals 和 Life Balance */}
      <div className="flex flex-col md:flex-row gap-6 mb-6">
        {/* Goals 區塊 */}
        {objectives.length > 0 && (
          <div className="w-full md:w-1/2 flex flex-col">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-gray-700" /> Objectives
            </h3>

            <div className="space-y-3 flex-1">
              {visibleObjectives.map((obj, index) => {
                const config = OBJECTIVE_CATEGORY_CONFIG[obj.objectiveCategory as keyof typeof OBJECTIVE_CATEGORY_CONFIG]
                  || OBJECTIVE_CATEGORY_CONFIG.work;

                return (
                  <div
                    key={obj.objectiveId}
                    className="p-3 rounded-lg border border-gray-200 bg-white transition-all duration-300 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center justify-between">
                      {/* Icon 和標題 */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg ${config.bgColor} flex-shrink-0`}>
                          <config.icon className={`w-4 h-4 ${config.iconColor}`} />
                        </div>
                        <h4 className="font-medium text-gray-900 truncate text-sm">
                          {obj.objectiveTitle}
                        </h4>
                      </div>

                      {/* Ripples 數字 */}
                      <AnimatedRippleNumber
                        value={obj.ripples}
                        className="text-xl font-bold text-sky-600 ml-3 flex-shrink-0"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 展開更多按鈕 */}
            {hasMoreObjectives && (
              <button
                onClick={() => setShowAllObjectives(!showAllObjectives)}
                className="w-full mt-3 py-2 text-sm text-sky-600 hover:text-sky-700 flex items-center justify-center gap-1"
              >
                {showAllObjectives ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {objectives.length - INITIAL_DISPLAY_COUNT} more
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Life Balance 區塊 */}
        {activeDimensions.length > 0 && (
          <div className="w-full md:w-1/2 flex flex-col">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-gray-700" /> Balance
            </h3>

            <div className="bg-white rounded-lg border border-gray-200 p-4 flex-1">
              {activeDimensions.map((dim) => (
                <LifeBalanceBar
                  key={dim.dimension}
                  dimension={dim.dimension}
                  ripples={dim.ripples}
                />
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
